import React, { useState, useEffect } from 'react';
import { Button, useSession } from '@cordata/buffet';
import { useDebouncedCallback } from 'use-debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  adminInstance as Admin,
  publicInstance as Public,
  toastSuccess,
  toastError,
} from '~/utils';
import Modal from '~/components/common/Modal';
import { MatchStatus, MatchType } from '~/utils/const/identity';

interface ManualMRNMatchModalProps {
  data: any;
  isOpen: boolean;
  setIsOpen: Function;
  afterManualMrnMatchSubmission: Function;
}

const ManualMRNMatchModal = ({
  data,
  isOpen,
  setIsOpen,
  afterManualMrnMatchSubmission,
}: ManualMRNMatchModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [manualMrn, setManualMrn] = useState('');
  const [error, setError] = useState('');
  const { current } = useSession();

  // can be one of 'idle', 'checking', 'taken', 'available', or 'error'
  const [MRNCheckStatus, setMRNCheckStatus] = useState('idle');

  const checkForExistingMRN = useDebouncedCallback(
    (value) => {
      Public
        .get('tbl_matchresults', {
          params: {
            lookuppersonid: value,
            match_status_id: MatchStatus.Approved,
            division_uid: current.attributes.currentDivisionUid,
          },
        })
        .then((r) => {
          if (r.data.data.length === 0) {
            setMRNCheckStatus('available');
          } else {
            setMRNCheckStatus('taken');
          }
        })
        .catch((e) => {
          console.error('An error occurred!', e);
          setMRNCheckStatus('error');
        });
    },
    200,
  );

  const [sitenameForCurrentDivision, setSitenameForCurrentDivision] = useState();

  useEffect(() => {
    Public
      .get('tbl_identity_source', {
        params: {
          division_uid: current.attributes.currentDivisionUid,
        },
      })
      .then(r => r.data.data)
      .then(r => {
        // tbl_identity_source.source_name === tbl_matchresults.sitename
        // ^ the column is named differently but this is the join that
        // list_view_cs_tbl_matchresults does to get the source_description for
        // the patient banner in identity
        setSitenameForCurrentDivision(r[0].attributes.sourceName);
      });
  }, []);

  useEffect(() => {
    setMRNCheckStatus('checking');

    if (manualMrn.length > 0) {
      checkForExistingMRN(manualMrn);
    } else {
      setMRNCheckStatus('idle');
    }
  }, [manualMrn]);

  const handleSubmit = () => {
    setIsSubmitting(true);
    setError('');

    if (!manualMrn.match(/^[A-Za-z0-9]+$/g)) {
      setError('Please use only alphanumeric characters for MRN.');
      setIsSubmitting(false);
      return;
    }

    const payload = {
      lookuppersonid: manualMrn,
      matchTypeId: MatchType.Manual,
      matchStatusId: MatchStatus.NeedToBeApproved,
      userModified: true,
      sourcepersonid: data.attributes.sacwisPersonId,
      sourcepersonfirstname: data.attributes.firstName,
      sourcepersonlastname: data.attributes.lastName,
      sourcepersondateofbirth: data.attributes.birthDate,
      sourcepersongender: data.attributes.gender,
      lookuppersonfirstname: data.attributes.firstName,
      lookuppersonlastname: data.attributes.lastName,
      lookuppersondateofbirth: data.attributes.birthDate,
      lookuppersongender: data.attributes.gender,
      sitename: sitenameForCurrentDivision,
      org_uid: localStorage.getItem('org'),
      division_uid: current.attributes.currentDivisionUid,
      patient_uid: data.attributes.patientUid,
      recordcreatedate: new Date(),
      match_status_date: new Date(),
      matchdate: new Date(),
      dispute_comment: 'Manual Match',

      // columns on tbl_matchresults that we are leaving empty for now:
      // sourcepersonpreviousid
      // matchalgorithmshortname
      // matchalgorithmconfidence
      // matchalgorithmid
    };

    Admin
      .post('tbl_matchresults', payload)
      .then((r) => {
        toastSuccess();
        setIsOpen(false);
        setIsSubmitting(false);

        afterManualMrnMatchSubmission(r);
      })
      .catch(toastError)
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Modal
      isOpen={isOpen}
      headerIcon="plus"
      title="Manual EHR Match"
      action={
        <>
          <Button outline type="cancel" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>

          <Button
            type="button"
            color="primary"
            disabled={isSubmitting || manualMrn.length === 0 || MRNCheckStatus !== 'available'}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Working ...' : 'Ready For Approval'}
          </Button>
        </>
      }
    >
      <p>
        You are manually matching a MRN to this SACWIS ID which requires additional review and approval. Once approved, the healthcare records for the patient will be extracted and made available as soon as possible.
      </p>

      <p>Please enter the MRN that should be matched to this SACWIS ID:</p>

      <input className="w-100" onChange={e => setManualMrn(e.target.value)} />
      { error && (
        <p className="text-danger">{error}</p>
      )}

      <small>
        {MRNCheckStatus === 'checking' && (
          <span className="text-info">
            <FontAwesomeIcon icon="spinner" spin /> Checking for existing MRNs...
          </span>
        )}

        {MRNCheckStatus === 'taken' && (
          <span className="text-danger">Sorry, that MRN is already in use.</span>
        )}

        {MRNCheckStatus === 'available' && (
          <span className="text-success">That MRN is not in use.</span>
        )}

        {MRNCheckStatus === 'error' && (
          <span className="text-danger">
            Sorry, an error occurred. Please contact a Cordata representative
            if this continues to happen.
          </span>
        )}
      </small>
    </Modal>
  );
};

export default ManualMRNMatchModal;
